import React from 'react';
import type { CanvasSectionBlock } from '../sections/sectionRegistry';
import { useFlowContext } from './FlowProvider';
import { findConnectedBlockNodeSummary, findConnectedBlockNodeSummaryInFlow } from './connectedBlocks';
import { VASE_FLOW_FIELD_IDS } from '../../lib/flows';
import { buildSelectionContext } from '../../lib/flowNodeListHelpers';
import { resolveFlowForBlock } from '../../lib/flowBlockResolution';

export function useConnectedBlockRuntime(
  block: CanvasSectionBlock,
  options?: { sectionId?: string | null }
) {
  const flow = useFlowContext();
  const resolvedFlow = React.useMemo(
    () => resolveFlowForBlock(flow.flows, { block, sectionId: options?.sectionId }),
    [block, flow.flows, options?.sectionId]
  );
  const summary = React.useMemo(
    () =>
      findConnectedBlockNodeSummaryInFlow(block, resolvedFlow) ??
      (!options?.sectionId ? findConnectedBlockNodeSummary(block, flow.flows) : null),
    [block, flow.flows, options?.sectionId, resolvedFlow]
  );
  const runtimeFlow = resolvedFlow ?? summary?.flow ?? null;

  const getSocketFieldId = React.useCallback(
    (direction: 'input' | 'output', socketId: string) => {
      const bindings = direction === 'input' ? summary?.node.inputs : summary?.node.outputs;
      return bindings?.find((binding) => binding.socketId === socketId)?.fieldId ?? null;
    },
    [summary]
  );

  const getSocketValue = React.useCallback(
    (direction: 'input' | 'output', socketId: string) => {
      const activeFlow = runtimeFlow ?? summary?.flow ?? null;
      if (!summary || !activeFlow) return undefined;
      const fieldId = getSocketFieldId(direction, socketId);

      if (!fieldId) {
        if (direction === 'input') {
          if (socketId === 'selection') {
            return buildSelectionContext({
              product: flow.getFlowValue(activeFlow.id, VASE_FLOW_FIELD_IDS.chosenProduct),
              finish: flow.getFlowValue(activeFlow.id, VASE_FLOW_FIELD_IDS.chosenFinish),
              colours: flow.getFlowValue(activeFlow.id, VASE_FLOW_FIELD_IDS.chosenColours),
              combination: flow.getFlowValue(activeFlow.id, VASE_FLOW_FIELD_IDS.chosenCombination),
            });
          }
          if (socketId === 'product') {
            return flow.getFlowValue(activeFlow.id, VASE_FLOW_FIELD_IDS.chosenProduct);
          }
          if (socketId === 'finish') {
            return flow.getFlowValue(activeFlow.id, VASE_FLOW_FIELD_IDS.chosenFinish);
          }
          if (socketId === 'colours') {
            return flow.getFlowValue(activeFlow.id, VASE_FLOW_FIELD_IDS.chosenColours);
          }
          if (socketId === 'combination') {
            return flow.getFlowValue(activeFlow.id, VASE_FLOW_FIELD_IDS.chosenCombination);
          }
        }
        return undefined;
      }

      return flow.getFlowValue(activeFlow.id, fieldId);
    },
    [flow, getSocketFieldId, runtimeFlow, summary]
  );

  return React.useMemo(
    () => ({
      summary,
      flowDefinition: runtimeFlow,
      flowId: runtimeFlow?.id ?? summary?.flow.id ?? null,
      getFlowValue: flow.getFlowValue,
      getSocketFieldId,
      getSocketValue,
      patchFlowValues: flow.patchFlowValues,
      invokeFlowNode: flow.invokeFlowNode,
    }),
    [
      flow.getFlowValue,
      flow.invokeFlowNode,
      flow.patchFlowValues,
      getSocketFieldId,
      getSocketValue,
      runtimeFlow,
      summary,
    ]
  );
}

export function buildSocketPatch(
  runtime: Pick<ReturnType<typeof useConnectedBlockRuntime>, 'getSocketFieldId'>,
  updates: Array<{
    direction: 'input' | 'output';
    socketId: string;
    value: unknown;
  }>
) {
  const patch: Record<string, unknown> = {};
  updates.forEach((update) => {
    const fieldId = runtime.getSocketFieldId(update.direction, update.socketId);
    if (fieldId) {
      patch[fieldId] = update.value;
    }
  });
  return patch;
}
